import { TypedDocumentNode, gql } from '@apollo/client';
import { faUser } from '@fortawesome/pro-solid-svg-icons';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import {
  Button,
  Props as ButtonProps,
} from '@sorare/core/src/atoms/buttons/Button';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import {
  BodyM,
  BodyS,
  HeadlineM,
  LabelM,
  LabelS,
} from '@sorare/core/src/atoms/typography';
import { Chip } from '@sorare/core/src/atoms/ui/Chip';
import { Variant } from '@sorare/core/src/components/rewards/AnyRewardConfig';
import { ANY_SPORT_PLAY_EVENTTYPE_FIXTURE_COMPETITION_COMPETITION_TRCKGRP_TRCK } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';
import { fantasy, glossary } from '@sorare/core/src/lib/glossary';
import { isType } from '@sorare/core/src/lib/gql';
import {
  laptopAndAbove,
  tabletAndAbove,
} from '@sorare/core/src/style/mediaQuery';

import { ClickableAnyRewardConfig } from 'components/rewards/ClickableAnyRewardConfig';

import type {
  CompetitionRow_So5Leaderboard,
  CompetitionRow_So5LeagueTrack,
} from './__generated__/index.graphql';

export const Row = styled.div`
  display: grid;
  grid-template-areas:
    'competition competition'
    'prizePool prizePool'
    'join join';
  grid-template-columns: 4fr 2fr;
  row-gap: var(--unit);

  padding: var(--intermediate-unit) var(--double-unit);
  border-top: 1px solid var(--c-nd-50);

  @media ${laptopAndAbove} {
    column-gap: var(--triple-unit);
    grid-template-columns: 3fr 3fr 0.5fr 0.5fr 1fr;
    grid-template-areas: 'competition prizePool players entries join';
  }
`;

const Wrapper = styled.div`
  position: relative;

  @media ${laptopAndAbove} {
    &:hover {
      background: var(--c-nd-50);
    }
  }
`;

const StyledJoinButton = styled(Button)`
  margin-left: auto;
  width: 100%;

  @media ${tabletAndAbove} {
    width: auto;
  }
`;

const LeaderboardIcon = styled.img`
  width: 48px;
  height: 48px;

  @media ${tabletAndAbove} {
    width: 56px;
    height: 56px;
  }
`;

const PlayerNbWrapper = styled(Horizontal)`
  margin-left: auto;
`;

const FullWidthLink = styled(Link)`
  width: 100%;
`;

type Props = {
  eventType: string;
  leaderboard: CompetitionRow_So5Leaderboard;
  currentTrack?: CompetitionRow_So5LeagueTrack;
};

type ResponsiveProps = {
  to: string;
  isFull?: boolean;
  leaderboard: CompetitionRow_So5Leaderboard;
};

type JoinButtonProps = {
  isFull?: boolean;
} & Pick<ResponsiveProps, 'leaderboard'> &
  ButtonProps;

const JoinButton = ({ isFull, leaderboard, ...rest }: JoinButtonProps) => {
  const canCompose = leaderboard.so5Fixture?.canCompose;

  if (canCompose) {
    return (
      <StyledJoinButton color={isFull ? 'tertiary' : 'primary'} {...rest}>
        {isFull ? (
          <FormattedMessage {...fantasy.joined} />
        ) : (
          <FormattedMessage {...glossary.join} />
        )}
      </StyledJoinButton>
    );
  }

  return (
    <StyledJoinButton disabled color="transparent" {...rest}>
      <FormattedMessage {...glossary.locked} />
    </StyledJoinButton>
  );
};

const MobileLayout = ({ to, leaderboard, isFull }: ResponsiveProps) => {
  return (
    <Row>
      <Vertical style={{ gridArea: 'competition' }}>
        <Horizontal>
          <LeaderboardIcon
            src={leaderboard.so5LeaderboardGroup.iconUrl || ''}
            alt=""
          />
          <BodyM bold>{leaderboard.displayName}</BodyM>
          <PlayerNbWrapper>
            <FontAwesomeIcon icon={faUser} size="xs" color="var(--c-nd-600)" />
            <LabelM color="var(--c-nd-600)">
              <FormattedNumber value={leaderboard.so5LineupsCount ?? 0} />
            </LabelM>
          </PlayerNbWrapper>
        </Horizontal>
        <BodyS color="var(--c-nd-600)">{leaderboard.description}</BodyS>
      </Vertical>

      <Horizontal style={{ gridArea: 'prizePool' }}>
        {leaderboard.totalRewardConfigs.map((anyRewardConfig, i) => (
          <ClickableAnyRewardConfig
            // eslint-disable-next-line react/no-array-index-key
            key={i}
            variant={Variant.INLINE}
            anyRewardConfig={anyRewardConfig}
            hideText={isType(anyRewardConfig, 'CardRewardConfig')}
          />
        ))}
      </Horizontal>
      <Vertical style={{ gridArea: 'join' }} center>
        <FullWidthLink to={to}>
          <JoinButton
            leaderboard={leaderboard}
            size="small"
            asSpan
            isFull={isFull}
          />
        </FullWidthLink>
      </Vertical>
    </Row>
  );
};

const DesktopLayout = ({
  to,
  leaderboard,
  isFull,
  myLineupsCount,
  maxManagerTeamsCount,
}: ResponsiveProps & {
  myLineupsCount?: number;
  maxManagerTeamsCount?: number;
}) => {
  return (
    <Link to={to}>
      <Row>
        <Vertical style={{ gridArea: 'competition' }}>
          <Horizontal>
            <LeaderboardIcon
              src={leaderboard.so5LeaderboardGroup.iconUrl || ''}
              alt=""
            />
            <Vertical gap={0.5}>
              <HeadlineM>{leaderboard.displayName}</HeadlineM>
              <BodyS color="var(--c-nd-600)">{leaderboard.description}</BodyS>
            </Vertical>
          </Horizontal>
        </Vertical>
        <Horizontal style={{ gridArea: 'players' }}>
          <FontAwesomeIcon icon={faUser} size="xs" color="var(--c-nd-600)" />
          <LabelS color="var(--c-nd-600)">
            <FormattedNumber value={leaderboard.so5LineupsCount ?? 0} />
          </LabelS>
        </Horizontal>
        <Horizontal style={{ gridArea: 'entries' }}>
          <Chip color={isFull ? 'green' : 'transparent'}>
            <FormattedNumber value={myLineupsCount ?? 0} />
            {' / '}
            <FormattedNumber value={maxManagerTeamsCount ?? 0} />
          </Chip>
        </Horizontal>
        <Horizontal style={{ gridArea: 'prizePool' }}>
          {leaderboard.totalRewardConfigs.map((anyRewardConfig, i) => (
            <ClickableAnyRewardConfig
              // eslint-disable-next-line react/no-array-index-key
              key={i}
              variant={Variant.INLINE}
              anyRewardConfig={anyRewardConfig}
              hideText={isType(anyRewardConfig, 'CardRewardConfig')}
            />
          ))}
        </Horizontal>
        <Vertical style={{ gridArea: 'join' }} center>
          <JoinButton
            asSpan
            to={to}
            size="small"
            isFull={isFull}
            leaderboard={leaderboard}
          />
        </Vertical>
      </Row>
    </Link>
  );
};

export const CompetitionRow = ({
  leaderboard,
  eventType,
  currentTrack,
}: Props) => {
  const isTabletAndAbove = useIsTabletAndAbove();
  const { generateSportContextPath } = useSportContext();
  const to = generateSportContextPath(
    ANY_SPORT_PLAY_EVENTTYPE_FIXTURE_COMPETITION_COMPETITION_TRCKGRP_TRCK,
    {
      params: {
        eventType,
        fixture: leaderboard.so5Fixture?.slug,
        competition: leaderboard.so5LeaderboardGroup?.slug,
        trckGrp: leaderboard?.so5LeagueTrack?.groupSlug,
        trck: leaderboard?.so5LeagueTrack?.slug,
      },
    }
  );

  const maxManagerTeamsCount = currentTrack?.maxManagerTeamsCount;
  const myLineupsCount = currentTrack?.mySo5LeaderboardContenders.filter(
    ({ so5Lineup }) => so5Lineup?.id
  ).length;

  const isFull = myLineupsCount === maxManagerTeamsCount;

  return (
    <Wrapper key={leaderboard.slug}>
      {isTabletAndAbove ? (
        <DesktopLayout
          to={to}
          isFull={isFull}
          leaderboard={leaderboard}
          myLineupsCount={myLineupsCount}
          maxManagerTeamsCount={maxManagerTeamsCount}
        />
      ) : (
        <MobileLayout to={to} isFull={isFull} leaderboard={leaderboard} />
      )}
    </Wrapper>
  );
};

CompetitionRow.fragments = {
  So5Leaderboard: gql`
    fragment CompetitionRow_So5Leaderboard on So5Leaderboard {
      slug
      iconUrl
      rarityType
      description
      displayName(withSeasonality: $withSeasonality, short: true)
      mainRarityType
      so5LineupsCount
      so5Fixture {
        slug
        canCompose
      }
      so5LeagueTrack {
        slug
        groupSlug
      }
      so5LeaderboardGroup(groupType: COMPETITION) {
        slug
        iconUrl
      }
      totalRewardConfigs {
        id
        ...ClickableAnyRewardConfig_anyRewardConfigInterface
      }
    }
    ${ClickableAnyRewardConfig.fragments.anyRewardConfigInterface}
  ` as TypedDocumentNode<CompetitionRow_So5Leaderboard>,
  So5LeagueTrack: gql`
    fragment CompetitionRow_So5LeagueTrack on So5LeagueTrack {
      slug
      maxManagerTeamsCount
      entrySo5Leaderboard {
        slug
      }
      mySo5LeaderboardContenders {
        slug
        so5Lineup {
          id
        }
      }
    }
  ` as TypedDocumentNode<CompetitionRow_So5LeagueTrack>,
};
